import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { trackDashboardCtaClicked } from "analytics";
import { PATHS } from "constants/pathConstants";
import {
  TEST_DISPLAY_NAME_BY_TYPE,
  TEST_NAME_BY_UUID
} from "components/v2/Pages/TestResultsPage/testLanguage";
import {
  selectExistingOrdersWithoutCancelled,
  selectTestKitsGLIsCurrentlyOffering
} from "store/reducers/order/OrderInformationSlice";
import { selectHasNewResultsToView } from "store/reducers/user/UserAccountSlice";
import { Kit } from "types/kit";
import NextMoveCard from ".";
import { GlContentTagVariant } from "components/v3/sharedComponents/GlContentTag";
import { SHOW_ALL_NEXT_MOVE_CARDS } from "./showAllNextMoveCards";
import Translate from "translate/Translate";

const testNameByUuid = TEST_NAME_BY_UUID as Record<string, string>;
const testDisplayNameByType = TEST_DISPLAY_NAME_BY_TYPE as Record<
  string,
  string
>;

const resultNameForOrder = (
  order: Kit,
  offeredKits: Array<{ glTestType?: string; name?: string; test_name?: string }>
): string => {
  const offered = offeredKits.find((kit) => kit.glTestType === order.glTestType);
  const orderUuid = order.glTestUUID || order.description?.uuid;
  return (
    offered?.name ||
    (orderUuid ? testNameByUuid[orderUuid] : undefined) ||
    testDisplayNameByType[order.glTestType] ||
    offered?.test_name ||
    order.glTestType
  );
};

/**
 * Viewed-or-not is only known for Genetic Health Screen, and only as one flag
 * for every GHS document. Other test types stay untagged.
 */
const tagForOrder = (
  order: Kit,
  hasNewResultsToView: boolean
): GlContentTagVariant | undefined => {
  if (order.glTestType !== "GHS") {
    return undefined;
  }

  return hasNewResultsToView ? "new" : "completed";
};

const ReviewResultsNextMoveCard = () => {
  const history = useHistory();
  const hasNewResultsToView = useSelector(selectHasNewResultsToView);
  const offeredKits = useSelector(selectTestKitsGLIsCurrentlyOffering);
  const orders = useSelector(selectExistingOrdersWithoutCancelled);
  const ordersWithResults = (orders as Kit[]).filter(
    (order) => order.documentIds?.length > 0
  );

  if (!SHOW_ALL_NEXT_MOVE_CARDS && ordersWithResults.length === 0) {
    return null;
  }

  const cards =
    ordersWithResults.length > 0
      ? ordersWithResults
      : [
          {
            glOrderId: "preview",
            glTestType: "GHS" as const,
            documentIds: ["preview"]
          } as Kit
        ];

  return (
    <>
      {cards.map((order) => {
        const testName = resultNameForOrder(order, offeredKits);
        const destination = `${PATHS.TEST_RESULTS_PAGE}/${order.glOrderId}`;

        return (
          <NextMoveCard
            key={order.glOrderId}
            id={`review-results-next-move-${order.glOrderId}`}
            tag={tagForOrder(order, hasNewResultsToView)}
            title={
              <Translate text={`Review your ${testName} results`} />
            }
            body={
              <Translate text="Your results are ready! After reviewing them, work with the Genomic Life navigation team and your doctor to take the next steps." />
            }
            subBody={
              <Translate text="If you have any questions about your results, simply send us an email at navigation@genomiclife.com" />
            }
            link={{
              onClick: () => {
                trackDashboardCtaClicked({
                  workflow: "results_review",
                  ctaLocation: "next_move_card",
                  ctaId: "test_result"
                });
                history.push(destination);
              },
              overrideClick: true,
              url: destination
            }}
          />
        );
      })}
    </>
  );
};

export default ReviewResultsNextMoveCard;
